import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useMoodboards } from '@/hooks/use-moodboards';
import { Button } from '@/components/ui/button';
import { CreateMoodboardDialog } from '@/components/moodboards/CreateMoodboardDialog';

export default function MoodboardsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: moodboards = [] } = useMoodboards(user?.id);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  return (
    <>
      <div className="min-h-screen bg-[#1A1A1B]">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/10">
          <div className="px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-xl font-bold text-white">Moodboards</h1>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <Plus className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Moodboards Grid */}
        <div className="max-w-5xl mx-auto px-4 py-6">
          {moodboards.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🎭</div>
              <h2 className="text-2xl font-bold mb-2">No Moodboards Yet</h2>
              <p className="text-muted-foreground mb-6">
                Create your first moodboard to start organizing your favorite content
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Moodboard
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {moodboards.map((moodboard, index) => (
                <motion.button
                  key={moodboard.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/moodboards/${moodboard.id}`)}
                  className="relative aspect-square rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-pink-400/50 transition-all group"
                >
                  {moodboard.cover_image_url ? (
                    <img
                      src={moodboard.cover_image_url}
                      alt={moodboard.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-5xl bg-gradient-to-br from-pink-500/10 to-purple-500/10">
                      {moodboard.icon}
                    </div>
                  )}
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Item count - top right */}
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm">
                    <p className="text-[10px] text-white/70 font-medium">{moodboard.item_count || 0}</p>
                  </div>
                  
                  {/* Name - bottom left */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-white truncate flex-1 drop-shadow-lg">{moodboard.name}</p>
                      {moodboard.is_private && (
                        <Lock className="w-3 h-3 text-white/70 flex-shrink-0 drop-shadow-lg" />
                      )}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <CreateMoodboardDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />
    </>
  );
}
